import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { createGithubUser, selectShouldShowNav } from "./index";
import {
  fetchUserProfileById,
  setIsAuthenticated,
  updateUserProfile,
} from "../UserProfile";
import { actions } from "../UserProfile/userProfile.reducers";
import { actions as authScreensActions } from "./authScreens.reducers";
import {
  EMPTY_STRING,
  GITHUB_AUTH_FLOW,
  ROUTES,
  VALIDATION_MESSAGES
} from "../../constants";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { selectAlert } from "../../components/organisms/Alert";
import { loginBg } from "../../assets/images";
import { Alert } from "../../components/molecules";

const { HOME } = ROUTES;
const { GITHUB_SIGNIN } = SPINNER_NAMES;
const LOGIN_ERROR_MESSAGE = "Unable to login. Please try again later.";
const { GITHUB, GITHUB_USER_INFO, PROVIDER } = GITHUB_AUTH_FLOW;
const { setShouldFetchUser } = actions;
const { setShouldShowNav } = authScreensActions;
const CODE = "code";
const { ORGANIZATION_REQUIRED } = VALIDATION_MESSAGES

const GithubRedirect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedAlert = useSelector(selectAlert);
  const shouldShowNav = useSelector(selectShouldShowNav);

  const [alertData, setAlertData] = useState(selectedAlert);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const validationSchema = Yup.object({
    organization: Yup.string().required(ORGANIZATION_REQUIRED),
  });

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  const handleOnSubmit = (values) => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get(CODE);
    if (code) {
      dispatch(
        createGithubUser({
          code,
          organizationName: values.organization,
          provider: GITHUB,
        })
      )
        .then((res) => {
          if (res.payload) {
            const { user } = res.payload;
            sessionStorage.setItem(PROVIDER, GITHUB);
            sessionStorage.setItem(
              GITHUB_USER_INFO,
              JSON.stringify(res.payload)
            );
            dispatch(setShouldFetchUser(false));
            dispatch(fetchUserProfileById(user.sub)).then((response) => {
              if (!isEmpty(response.payload)) {
                dispatch(
                  updateUserProfile({
                    id: response.payload.id,
                    user: {
                      lastSignIn: new Date(),
                    },
                  })
                ).then(() => {
                  dispatch(setIsAuthenticated(true));
                  dispatch(setShouldShowNav(true));
                  setTimeout(navigate(HOME, { replace: true }), 1000);
                  window.location.reload();
                });
              } else {
                sessionStorage.removeItem(PROVIDER);
                sessionStorage.removeItem(GITHUB_USER_INFO);
                setAlertData({ message: LOGIN_ERROR_MESSAGE });
              }
            });
          }
        })
        .catch((error) => setAlertData({ message: error.message }));
    }
  };

  return (
    <>
      <Helmet>
        <title>Github | TavaJS</title>
      </Helmet>
      {!shouldShowNav && (
        <div className="auth-container min-h-screen relative flex items-center">
          <img
            src={loginBg}
            className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
            alt="login-bg"
          />
          <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
            <div className="auth-header mb-8">
              {alertData && (
                <div className="my-2">
                  <Alert
                    type={alertData.type}
                    message={alertData.message}
                    handleOnClose={setAlertData}
                  />
                </div>
              )}
              SignIn via github successful.
            </div>
            <div className="auth-body">
              <Formik
                initialValues={{ organization: EMPTY_STRING }}
                validationSchema={validationSchema}
                onSubmit={handleOnSubmit}
                enableReinitialize
              >
                {({
                  values,
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  touched,
                  errors,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <div className="form-group mb-6">
                      <label className="text-sm font-medium mb-1 block">
                        Enter Your Organization Name
                      </label>
                      <Field
                        type="text"
                        className={classNames("form-control border-2", {
                          "border-red-600":
                            touched.organization && errors.organization,
                        })}
                        placeholder="Your Organization"
                        name="organization"
                        value={values.organization}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {touched.organization && errors.organization && (
                        <div className="font-medium text-red-500 text-sm absolute">
                          {errors.organization}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn btn-primary block w-full text-center"
                      type="submit"
                      disabled={isSpinnerActive}
                    >
                      <Spinner
                        name={GITHUB_SIGNIN}
                        setIsSpinnerActive={setIsSpinnerActive}
                        persistSize
                      >
                        Submit
                      </Spinner>
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GithubRedirect;
